import type { VideoJsPlayer } from 'video.js';
import videojs from 'video.js';
import './style.scss';

interface PlaylistOptions {
  src: string[];
  isLive?: boolean;
  ui?: boolean;
}

export class Playlist extends videojs.getPlugin('plugin') {
  private options: PlaylistOptions = { src: [], ui: false };
  private onChange: ((index: number) => void) | undefined;

  private ui!: {
    container: HTMLElement;
    next: HTMLElement;
    prev: HTMLElement;
  };

  public constructor(player: VideoJsPlayer, options?: PlaylistOptions) {
    super(player, options);

    this.setup();
  }

  private setup(): void {
    this.ui = {
      container: document.createElement('div'),
      next: document.createElement('div'),
      prev: document.createElement('div')
    };

    const { container, next, prev } = this.ui;

    container.classList.add('vjs-playlist');
    next.classList.add('vjs-playlist-next');
    prev.classList.add('vjs-playlist-prev');

    container.append(prev, next);

    prev.addEventListener('click', this.playPrevious);
    next.addEventListener('click', this.playNext);

    this.player.el().append(container);
  }

  public update(options: PlaylistOptions, onChange?: (index: number) => void): void {
    this.options = options;
    this.onChange = onChange;

    this.init();
  }

  private init(): void {
    const { src, ui, isLive } = this.options;

    if (src.length < 1 || !ui) {
      this.ui.container.classList.add('no-playlist-ui');
    } else {
      this.ui.container.classList.remove('no-playlist-ui');
    }

    if (isLive) {
      this.playIndex(src.length - 1);
    } else {
      this.playIndex(0);
    }

    this.player.on('ended', this.playNext);
  }

  private get currentIndex(): number {
    const currentSource = this.player.src();
    const { src } = this.options;

    return src.indexOf(currentSource);
  }

  private readonly playIndex = (index: number): void => {
    const { src } = this.options;

    if (src[index]) {
      this.playSource(src[index]);
      this.onChange?.(index);
    }

    // Update UI.
    this.ui.prev.classList.add('disable');
    this.ui.next.classList.add('disable');

    if (src[index + 1]) {
      this.ui.next.classList.remove('disable');
    }

    if (src[index - 1]) {
      this.ui.prev.classList.remove('disable');
    }
  };

  private playSource(src: string): void {
    this.player.src(src);
    this.player.load();

    void this.player.play()?.catch(() => {
      // Ignore.
    });
  }

  public playNext = (): void => {
    this.playIndex(this.currentIndex + 1);
  };

  public playPrevious = (): void => {
    this.playIndex(this.currentIndex - 1);
  };
}
